import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/lib/auth';
import { ReferralsService, type Referral } from '@/data/referrals.service';

/**
 * Hook to get or create referral code
 */
export function useReferralCode() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: ['referral-code', user?.id],
    queryFn: async (): Promise<string> => {
      if (!user?.id) throw new Error('No user');
      // Function uses auth.uid() internally, no parameters needed
      return ReferralsService.getOrCreateReferralCode();
    },
    enabled: !!user?.id,
    staleTime: Infinity, // Referral code doesn't change
  });
}

/**
 * Hook to get referral stats
 */
export function useReferralStats() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['referral-stats', user?.id],
    queryFn: async () => {
      if (!user?.id) throw new Error('No user');
      return ReferralsService.getReferralStats(user.id);
    },
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to get referral link
 */
export function useReferralLink() {
  const { data: code } = useReferralCode();
  
  const referralLink = code
    ? `${window.location.origin}/?ref=${code}`
    : null;

  return { code, referralLink };
}

